import {Dialog} from '@primer/react/experimental'
import {BUTTON_LABELS, LABELS, VALUES} from '../..'
import {
  RepositoryPicker,
  type RepositoryPickerRepository$data,
  type RepositoryPickerTopRepositoriesQuery,
  TopRepositories,
} from '@github-ui/item-picker/repository'
import {type PreloadedQuery, useQueryLoader, useRelayEnvironment} from 'react-relay'
import {useCallback, useEffect, useState} from 'react'
import {commitTransferIssueMutation} from '../../mutations/transfer-issue-mutation'
import type {TransferIssueInput} from '../../mutations/__generated__/transferIssueMutation.graphql'
import {useToastContext} from '@github-ui/toast'
import {ERRORS} from '../../constants'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {reportError} from '@github-ui/failbot'
import {Flash, Link} from '@primer/react'

type IssueTransferDialogProps = {
  owner: string
  repository: string
  issueId: string
  isTransferInProgress?: boolean
  isRepoPrivate?: boolean
  onClose: () => void
}
export const IssueTransferDialog = (props: IssueTransferDialogProps) => {
  const [topReposQueryRef, loadTopRepos, disposeTopRepos] =
    useQueryLoader<RepositoryPickerTopRepositoriesQuery>(TopRepositories)

  useEffect(() => {
    loadTopRepos({topRepositoriesFirst: VALUES.issueTransferRepositoriesPreloadCount, hasIssuesEnabled: true})

    return () => {
      disposeTopRepos()
    }
  }, [disposeTopRepos, loadTopRepos])

  if (!topReposQueryRef) return null

  return <IssueTransferDialogInternal {...props} topReposQueryRef={topReposQueryRef} />
}

type IssueTransferDialogPropsInternal = IssueTransferDialogProps & {
  topReposQueryRef: PreloadedQuery<RepositoryPickerTopRepositoriesQuery>
}
export const IssueTransferDialogInternal = ({
  topReposQueryRef,
  owner,
  repository,
  isRepoPrivate,
  issueId,
  onClose,
  ...props
}: IssueTransferDialogPropsInternal) => {
  const [selectedRepository, setSelectedRepository] = useState<RepositoryPickerRepository$data>()
  const [isTransferring, setIsTransferring] = useState(props.isTransferInProgress)
  const [showWarningRepoUnavailable, setShowWarningRepoUnavailable] = useState(false)
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const handleTransferIssue = useCallback(() => {
    if (!selectedRepository || isTransferring) return
    setIsTransferring(true)

    const input: TransferIssueInput = {
      repositoryId: selectedRepository.id,
      issueId,
    }

    commitTransferIssueMutation({
      environment,
      input,
      onError: (e: Error) => {
        onClose()
        reportError(formatError(e.message))
        addToast({
          type: 'error',
          message: e.message,
        })
      },
      onCompleted: response => {
        onClose()
        if (response.transferIssue?.issue) {
          addToast({
            type: 'info',
            message: LABELS.transfer.started,
          })
          const issue = response.transferIssue?.issue
          if (ssrSafeWindow) {
            ssrSafeWindow.location.href = issue.url
          }
          return
        } else {
          for (const e of response.transferIssue?.errors || []) {
            reportError(formatError(e.message))
          }
          addToast({
            type: 'error',
            message: ERRORS.couldNotStartTransfer,
          })
        }
      },
    })
  }, [addToast, environment, isTransferring, issueId, onClose, selectedRepository])
  const onSelectedRepository = useCallback(
    (repo?: RepositoryPickerRepository$data) => {
      setShowWarningRepoUnavailable(false)
      setSelectedRepository(repo)

      if (!repo) return
      if (!repo.hasIssuesEnabled || repo.isArchived || (isRepoPrivate && !repo.isPrivate)) {
        setShowWarningRepoUnavailable(true)
      }
    },
    [isRepoPrivate],
  )

  const handleOnClose = useCallback(() => {
    setShowWarningRepoUnavailable(false)
    onClose()
  }, [onClose])

  return (
    <Dialog
      title={LABELS.transfer.title}
      subtitle={LABELS.transfer.subtitle}
      onClose={handleOnClose}
      footerButtons={[
        {content: BUTTON_LABELS.cancel, onClick: handleOnClose, disabled: isTransferring},
        {
          content: BUTTON_LABELS.transferIssue,
          buttonType: 'primary',
          onClick: handleTransferIssue,
          disabled: !selectedRepository || isTransferring,
        },
      ]}
    >
      <RepositoryPicker
        topReposQueryRef={topReposQueryRef}
        initialRepository={selectedRepository}
        organization={owner}
        options={{readonly: isTransferring}}
        exclude={`${owner}/${repository}`}
        onSelect={onSelectedRepository}
      />
      {showWarningRepoUnavailable && (
        <Flash aria-live="polite" sx={{mt: 3}}>
          {LABELS.transfer.repoUnavailable}
          <Link
            as="a"
            target="_blank"
            href="https://docs.github.com/en/issues/tracking-your-work-with-issues/transferring-an-issue-to-another-repository"
          >
            {' '}
            Learn more
          </Link>
        </Flash>
      )}
    </Dialog>
  )
}

function formatError(message: string) {
  return new Error(`Issue transfer mutation failed with error:${message}`)
}

try{ IssueTransferDialog.displayName ||= 'IssueTransferDialog' } catch {}
try{ IssueTransferDialogInternal.displayName ||= 'IssueTransferDialogInternal' } catch {}